import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { chatbotService } from '../../../services/chatbotService';
import { Button } from '../../ui/Button';
import { SelectField, TextField } from '../../ui/Field';
import { EntryChoiceButton, EntryChoicesConfig } from '../types/chatbot.types';

interface ConfigurationsTabProps {
  chatbot: any;
}

const DEFAULT_CONFIG: EntryChoicesConfig = {
  enabled: false,
  buttons: [
    { id: 'faq', label: 'Ask a question', intent: 'faq' },
    { id: 'booking', label: 'Book an appointment', intent: 'booking' },
  ],
};

const makeButtonId = () => `btn_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const ConfigurationsTab: React.FC<ConfigurationsTabProps> = ({ chatbot }) => {
  const [config, setConfig] = useState<EntryChoicesConfig>(DEFAULT_CONFIG);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!chatbot) return;
    const saved = chatbot.config?.entryChoices;
    setConfig({
      enabled: saved?.enabled ?? false,
      // Only fall back to the defaults when this chatbot has never saved entry-choice config at
      // all (saved.buttons isn't an array yet). A saved EMPTY array is a deliberate choice — the
      // admin removed every button — and must stay empty, not silently repopulate on reload.
      buttons: Array.isArray(saved?.buttons) ? saved.buttons : DEFAULT_CONFIG.buttons,
    });
  }, [chatbot?.id, chatbot?.updatedAt]);

  const updateButton = (id: string, patch: Partial<EntryChoiceButton>) => {
    setConfig((prev) => ({
      ...prev,
      buttons: prev.buttons.map((b) => (b.id === id ? { ...b, ...patch } : b)),
    }));
  };

  const addButton = () => {
    setConfig((prev) => ({
      ...prev,
      buttons: [...prev.buttons, { id: makeButtonId(), label: '', intent: 'faq' }],
    }));
  };

  const removeButton = (id: string) => {
    setConfig((prev) => ({ ...prev, buttons: prev.buttons.filter((b) => b.id !== id) }));
  };

  const save = async () => {
    if (!chatbot?.id) {
      toast.error('Missing chatbot id — cannot save.');
      return;
    }
    if (config.enabled && config.buttons.some((b) => !b.label.trim())) {
      toast.error('Every button needs a label.');
      return;
    }

    setIsSaving(true);
    try {
      const payload: any = {
        name: chatbot.name,
        description: chatbot.description,
        isActive: chatbot.isActive ?? true,
        state: chatbot.state ?? 'draft',
        version: chatbot.version ?? 1,
        config: {
          ...(chatbot.config ?? {}),
          entryChoices: config,
        },
      };
      await chatbotService.updateChatbot(encodeURIComponent(chatbot.id), payload);
      toast.success('Configurations saved.');
    } catch (err: any) {
      console.error('Failed to save entry choice configuration', err);
      toast.error(err?.message ?? 'Failed to save configurations');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-agentic-text-primary">Configurations</h2>
        <p className="text-sm text-agentic-text-secondary mt-1">
          Additional behavior settings for this chatbot.
        </p>
      </div>

      <section className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <label className="flex items-center gap-3 cursor-pointer w-fit">
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => setConfig((prev) => ({ ...prev, enabled: e.target.checked }))}
            className="h-4 w-4 rounded border-gray-300 text-agentic-primary-blue focus:ring-agentic-primary-blue"
          />
          <span className="text-sm font-medium text-agentic-text-primary">
            Show entry choice buttons after the welcome message
          </span>
        </label>

        {config.enabled && (
          <div className="space-y-2">
            {config.buttons.map((btn, i) => (
              <div key={btn.id} className="flex items-center gap-2">
                <span className="text-xs text-agentic-text-secondary w-4 shrink-0">{i + 1}.</span>
                <TextField
                  tone="default-focus-primary"
                  value={btn.label}
                  onChange={(e) => updateButton(btn.id, { label: e.target.value })}
                  placeholder="Button label, e.g. Book an appointment"
                  className="flex-1 min-w-0 px-3 py-1.5 rounded-lg border text-sm"
                />
                <SelectField
                  tone="default-focus-primary"
                  value={btn.intent}
                  onChange={(e) => updateButton(btn.id, { intent: e.target.value as EntryChoiceButton['intent'] })}
                  className="px-2 py-1.5 rounded-lg border text-sm shrink-0"
                >
                  <option value="faq">FAQ</option>
                  <option value="booking">Booking</option>
                </SelectField>
                <Button
                  tone="custom"
                  type="button"
                  onClick={() => removeButton(btn.id)}
                  className="shrink-0 px-1.5 py-1.5 text-sm text-gray-400 hover:text-red-600 rounded-lg"
                  aria-label="Remove button"
                >
                  ✕
                </Button>
              </div>
            ))}

            <Button
              tone="custom"
              type="button"
              onClick={addButton}
              className="text-sm font-medium text-agentic-primary-blue hover:text-agentic-primary-blue-dark"
            >
              + Add button
            </Button>
          </div>
        )}
      </section>

      <div className="flex justify-end">
        <Button
          tone="custom"
          onClick={save}
          disabled={isSaving}
          className={`px-4 py-2 rounded-lg text-white ${
            isSaving ? 'bg-gray-300 cursor-not-allowed' : 'bg-agentic-primary-blue hover:bg-agentic-primary-blue/90'
          }`}
        >
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

export default ConfigurationsTab;
